import { ReactNode, CSSProperties } from 'react';
import { toast, ToastOptions, ToastPosition, Flip } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DebugEx } from "@Utils/DebugEx";
import { isDefined } from '@Utils/Micro';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { renderInfoTip, InfoTipItem, AdvancedInfoTip, InfoTipLayout } from "./InfoTips";
import './ToastNotification.less';
/////////////////////////////////////////////////////////////////////////
export interface ToastNotificationPortData {
    /**
     * An id of the notification to avoid duplicate showing
     */
    ToastId?: string;

    /**
     * Toast type
     * Allowed values: 'info' | 'success' | 'warning' | 'error' | 'default'
     */
    Type: 'info' | 'success' | 'warning' | 'error' | 'default';

    /**
     * Toast position.
     * Allowed values: 'top-right', 'top-center', 'top-left', 'bottom-right', 'bottom-center', 'bottom-left'
     */
    Position: ToastPosition;

    /**
     * The Font Awesome icon (its name, prefixed with fa-).
     */
    Icon: string;

    /**
     * Toast's body content
     */
    Body: string;

    /**
     * Toast's title content
     * Invalid if the Body is ReactNode or NULL
     */
    Title: string;

    /**
     * Advanced Body
     */
    AdvanceBody: AdvancedInfoTip;

    /**
     * Whether automatically close or milliseconds to toast's expiration.
     */
    Autoclose: false | number;

    /**
     * Whether to show close button or not.
     */
    CloseButton: boolean;

    /**
     * Whether to indicate how long before a toast expires or not.
     */
    HideProgressBar: boolean;

    /**
     * Override style in settings
     */
    AdvanceBodyStyle?: CSSProperties;
}
/////////////////////////////////////////////////////////////////////////
export function ToastNotificationSchemeValidator(): JsonSchemaValidator {
    return JsonSchemaValidator.GetInstance("ToastNotificationSchemeValidator", {
        type: "object",
        // TODO: Define all objects
        properties: {
            Type: {
                type: "string",
                enum: ["info", "success", "warning", "error", "default"]
            },
            Position: {
                type: "string",
                enum: ["top-left", "top-right", "top-center", "bottom-left", "bottom-right", "bottom-center"]
            },
            Icon: {
                type: "string",
            },
            Body: {
                type: ["string"],
            },
            AdvanceBody: {
                // todo: add AdvancedInfoTip validator 
                type: ["object"],
            },
            Title: {
                type: "string",
            },
            Autoclose: {
                type: ["boolean", "number"],
            },
            CloseButton: {
                type: "boolean",
            }
        },
        required: ["Type"]
    });
}
// ------------------------------------------------------------------------------------------------------
export function showToastNotification(
    data: ToastNotificationPortData,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip) => void
) {
    try {
        // create toast options (if optional fields aren't defined use default values)
        var options: ToastOptions = {
            autoClose: (data.Autoclose != null) ? data.Autoclose : 5000,
            closeButton: (data.CloseButton) ? <i className="fas fa-times"></i> : false,
            hideProgressBar: (data.HideProgressBar != null) ? data.HideProgressBar : false,
            position: (data.Position != null) ? data.Position : 'top-right',
            transition: Flip,

            // prevent duplicates
            toastId: data.ToastId,
            theme: "colored",
            icon: false
        } as ToastOptions;

        let body: ReactNode = <div style={{
            alignItems: "center",
            display: "flex",
            margin: "5px 0 0 0",
            padding: 10,
            justifyContent: "flex-start",
            zIndex: 50000
        }}>
            <i
                className={data.Icon}
                color="inherit"
                style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    width: 0
                }} />
            <div style={{
                display: "flex",
                flexDirection: "column",
                flex: "7 0 0",
                padding: "0 0 0 20px"
            }}>
                <h2 style={{
                    fontSize: "large",
                    fontWeight: "bold",
                    padding: 0,
                    margin: 0
                }}>
                    {data.Title}
                </h2>
                <div style={{
                    fontSize: "small",
                    padding: "5px 0 0 0",
                    margin: 0
                }}>
                    {data.Body}
                </div>
            </div>
        </div>

        if (isDefined(data.AdvanceBody)) {
            body = renderInfoTip(
                "toast-advance",
                null,
                data.AdvanceBody,
                {
                    Layout: InfoTipLayout.Default,
                    FontSize: 11,
                    FontColor: "black",
                    BackgroundColor: "rgba(245, 245, 245, 0.75)",
                    BorderRadius: 8,
                    cardStyle: data.AdvanceBodyStyle
                },
                isDefined(handlePopupDataChange) ?
                    handlePopupDataChange :
                    (i) => { console.error("UnControlledButtons") }
            )
        }

        // open toast
        switch (data.Type) {
            case "info":
                toast.info(body, options);
                break;
            case "success":
                toast.success(body, options);
                break;
            case "warning":
                toast.warn(body, options);
                break;
            case "error":
                toast.error(body, options);
                break;
            case "default":
            default: {
                options.className = "toast-notification-default";
                toast(body, options);
                break;
            }

        }
    } catch (error) {
        DebugEx.TraceError(error);
    }
}
// ------------------------------------------------------------------------------------------------------